import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Input } from "@/src/components/ui/input"
import { Button } from "@/src/components/ui/button"
import { useState } from "react"
import { authClient } from "@/src/lib/auth-client"
import { useTranslation } from "@/src/i18n/client"

export const Route = createFileRoute('/_auth/register')({
  component: RegisterPage,
})

function RegisterPage() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    terms: false,
  })
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    
    if (form.password !== form.confirmPassword) {
      setError(t('register.errorPasswordMatch'))
      return
    }
    
    if (!form.terms) {
      setError(t('register.errorTerms'))
      return
    }
    
    setIsLoading(true)
    
    try {
      const result = await authClient.signUp.email({
        name: form.name,
        email: form.email,
        password: form.password,
      })
      
      if (result.error) {
        setError(result.error.message || t('register.errorRegistration'))
      } else {
        // Registration successful, redirect to login or dashboard
        navigate({ to: "/login" })
      }
    } catch (err) {
      setError(t('register.errorRegistrationRetry'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <h2 className="mb-6 text-center text-2xl font-bold text-foreground">{t('register.title')}</h2>
      {error && <div className="mb-4 text-red-500 text-center">{error}</div>}
      {/* Registration form */}
      <form className="space-y-6" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-foreground">{t('register.fullName')}</label>
          <Input id="name" name="name" type="text" required value={form.name} onChange={handleChange} className="h-10 mt-1 block w-full rounded-md border border-input bg-background text-foreground shadow-sm focus:border-primary focus:ring-primary" />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground">{t('register.email')}</label>
          <Input id="email" name="email" type="email" required value={form.email} onChange={handleChange} className="h-10 mt-1 block w-full rounded-md border border-input bg-background text-foreground shadow-sm focus:border-primary focus:ring-primary" />
        </div>
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-foreground">{t('register.password')}</label>
          <Input id="password" name="password" type="password" required value={form.password} onChange={handleChange} className="h-10 mt-1 block w-full rounded-md border border-input bg-background text-foreground shadow-sm focus:border-primary focus:ring-primary" />
        </div>
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground">{t('register.confirmPassword')}</label>
          <Input id="confirmPassword" name="confirmPassword" type="password" required value={form.confirmPassword} onChange={handleChange} className="h-10 mt-1 block w-full rounded-md border border-input bg-background text-foreground shadow-sm focus:border-primary focus:ring-primary" />
        </div>
        <div className="flex items-center">
          <input id="terms" name="terms" type="checkbox" required checked={form.terms} onChange={handleChange} className="h-4 w-4 rounded border-input text-primary focus:ring-primary" />
          <label htmlFor="terms" className="ml-2 block text-sm text-foreground">
            {t('register.terms')} <Link to="/terms" className="underline text-primary hover:text-primary/80">{t('register.termsOfService')}</Link> {t('register.and')} <Link to="/privacy" className="underline text-primary hover:text-primary/80">{t('register.privacyPolicy')}</Link>
          </label>
        </div>
        <div>
          <Button type="submit" disabled={isLoading} className="flex w-full justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2">
            {isLoading ? t('register.creating') : t('register.createButton')}
          </Button>
        </div>
      </form>
      <p className="mt-4 text-center text-sm text-muted-foreground">
        {t('register.haveAccount')} <Link to="/login" className="font-medium text-primary hover:text-primary/80">{t('register.signIn')}</Link>
      </p>
    </div>
  )
}
